import {
  CommonIntent,
  CustomData,
  CustomIntent,
  CustomIntentLink,
  DehydratedDesignIntent,
  DehydratedDesignIntentConfig,
  Design,
  DesignIntent,
  DesignIntentConfig,
  DesignItemType,
  DesignRootSettings,
  DsidIntent,
  IconClass,
  IntentApplication,
  NodeTypeName,
  Option,
  Public
} from '../types';
import { mergeArrays, pick } from '../utilities';
import Seed from '../utilities/Seed';
import Utility from '../utilities/Utility';
import { IHttpService } from './http-service';
import {
  HttpMethod,
  IntentUtteranceGenerationProps,
  IntentUtteranceGenerationResponse
} from './types';

/** Fields required to hydrate an intent's config. */
type IntentHydrateConfig = Omit<DesignIntentConfig, keyof (DehydratedDesignIntentConfig)>;

/** Public interface of @see IntentManagementService. */
export type IIntentManagementService = Public<IntentManagementService>;

export class IntentManagementService {
  /** Base URL to add to all HTTP requests. */
  static readonly baseUrl = '/ims';
  private _httpService: IHttpService;

  constructor(
    httpService: IHttpService
  ) {
    this._httpService = httpService;
  }

  /** Add @see CustomIntent data to @see DesignIntent data. */
  dehydrateDesign(design: Design): {
    avsIntents: DehydratedDesignIntent[];
    customIntents: CustomIntent[];
  } {
    const { features } = design.rootSettings;
    const avsIntents: DehydratedDesignIntent[] = [];
    const customIntents: CustomIntent[] = [];
    const designIntents = design.components.intents;
    if (!features.intentManagement) {
      return {
        avsIntents: designIntents,
        customIntents
      };
    }

    designIntents.forEach((intent): void => {
      const existingConfig = intent.props.config;
      const name = intent.name;
      const {
        children,
        commonIntent,
        description,
        localeData,
        parents
      } = existingConfig;

      customIntents.push({
        children,
        commonIntent,
        customId: intent.id,
        description,
        localeData,
        name,
        parents
      });

      // Do not actually remove properties from AVS intent because IMS save may fail.
      avsIntents.push(intent);
    });

    return {
      avsIntents,
      customIntents
    };
  }

  /** Get @see CommonIntent data from IMS. */
  async getCommonIntents(): Promise<CommonIntent[]> {
    const [
      dsidIntents,
      rawCommonIntents
    ] = await Promise.all([
      this.getDsidIntents(),
      this._httpService.fetchJson<CommonIntent[]>({
        url: 'intents/common'
      })
    ]);

    return mergeArrays(
      rawCommonIntents.value ?? [],
      dsidIntents,
      'name',
      'intent',
      ['countUsage']
    );
  }

  /** Get @see CustomIntent data from IMS. */
  async getCustomIntents({
    branchId,
    version
  }: Design): Promise<CustomIntent[]> {
    return (await this._httpService.fetchJson<CustomData>({
      url: `customdata/branches/${branchId}/versions/${version}`
    })).value?.customIntents ?? [];
  }

  async getDsidIntents(): Promise<DsidIntent[]> {
    return (await this._httpService.fetchJson<DsidIntent[]>({
      url: 'statistics/intent/dsid-intents'
    })).value ?? [];
  }

  async getIntentApplications(intentName: string): Promise<IntentApplication[]> {
    return (await this._httpService.fetchJson<IntentApplication[]>({
      url: `intents/${intentName}/applications`
    })).value ?? [];
  }

  /** Add @see CustomIntent data to @see DesignIntent data. */
  async hydrateDesign(design: Design): Promise<void> {
    // Partial is needed because this is called prior to `normalizeSerialized`.
    const { features } = (design.rootSettings ?? {}) as Partial<DesignRootSettings>;
    if (
      !features?.intentManagement
      || this.isHydrated(design)
    ) {
      return;
    }

    const designIntentMap = new Map(
      design.components.intents.map((designIntent) => {
        return [designIntent.id, designIntent];
      })
    );
    let imsIntents: CustomIntent[] = [];
    // This try/catch is needed because the first request will return 404.
    try {
      imsIntents = await this.getCustomIntents(design);
    } catch {
      // Intentionally left blank.
    }
    const imsIntentMap = new Map(imsIntents.map((imsIntent) => {
      return [imsIntent.customId, imsIntent];
    }));

    /** Create a @see CustomIntentLink for IMS from a DesignIntent. **/
    const getLinkedIntent = (id: string): CustomIntentLink => {
      const designIntent = designIntentMap.get(id);
      const { description } = designIntent?.props.config ?? {};

      if (!designIntent) {
        throw new Error(`Intent with ID '${id}' not found.`);
      }

      return {
        customId: designIntent.id,
        description: description ?? '',
        name: designIntent.name
      };
    };

    design.components.intents.forEach((intent) => {
      const imsIntent = imsIntentMap.get(intent.id);

      if (imsIntent) {
        const fieldsToAdd: IntentHydrateConfig = pick(imsIntent, [
          'children',
          'commonIntent',
          'droppable',
          'localeData',
          'parents'
        ]);
        intent.props.config = {
          ...intent.props.config,
          ...fieldsToAdd
        };
        return;
      }

      // Upgrade an existing intent.
      const existingConfig = intent.props.config;
      const fieldsToAdd: IntentHydrateConfig = {
        children: (existingConfig.childIntents?.map(getLinkedIntent) ?? []),
        commonIntent: null,
        droppable: existingConfig.droppable ?? false,
        localeData: {},
        parents: (existingConfig.parentIntent) ? (
          [getLinkedIntent(existingConfig.parentIntent)]
        ) : []
      };
      intent.props.config = {
        ...intent.props.config,
        ...fieldsToAdd
      };
      intent.props.lastLocalChange = window.apm?.timestamp ?? Date.now();
    });
  }

  /** Create a new intent to be added to a @see Design. */
  initializeIntent(useDefaultValues = true): DesignIntent {
    const { serialized, timestamp } = window.apm;
    const { features } = serialized.rootSettings;
    const intent = Seed.getElements().intents[0].items[0];
    const fieldsToAdd = (features.intentManagement) ? {
      children: [],
      commonIntent: null,
      droppable: false,
      localeData: {},
      parents: []
    } satisfies IntentHydrateConfig : null;

    return {
      ...intent,
      _class: 'IntentDataModel',
      id: Utility.uuid4(),
      name: (!useDefaultValues) ? '' : intent.name,
      props: {
        ...intent.props,
        config: {
          ...fieldsToAdd,
          ...intent.props.config,
          description: (!useDefaultValues) ? '' : intent.props.config.description
        },
        createdTime: timestamp,
        iconClass: IconClass.radio,
        lastLocalChange: timestamp,
        lastSavedChange: -1,
        nodeTypeName: NodeTypeName.intent
      },
      type: DesignItemType.intent
    };
  }

  /** Checks to see if all intents on @see design are hydrated by IMS. */
  isHydrated(design: Design): boolean {
    return design.components.intents.every((intent) => {
      return intent.props.config.localeData;
    });
  }

  /** Save CustomIntents and Entitys to IMS. */
  async saveCustomIntents(
    customIntents: CustomIntent[],
    branchId: string,
    version: string
  ): Promise<CustomIntent[]> {
    const body: CustomData = {
      customIntents
    };
    const { error, value } = await this._httpService.fetchJson<CustomIntent[]>({
      body,
      method: HttpMethod.post,
      url: `customdata/branches/${branchId}/versions/${version}`
    });

    if (error) {
      throw error;
    }

    return value;
  }

  async getLanguageModelOptions(): Promise<Option<string>[]> {
    return [
      {
        label: 'ChatGPT',
        value: 'chatgpt'
      }
    ];
  }

  /** Get generated phrases from IMS */
  async generatedUtterances({
    context,
    intent: { name },
    provider
  }: IntentUtteranceGenerationProps): Promise<string[]> {
    const { error, value } = await this._httpService.fetchJson<IntentUtteranceGenerationResponse>({
      method: HttpMethod.post,
      url: `phrases/${encodeURIComponent(name)}`,
      body: {
        context,
        intent: name,
        provider
      }
    });
    if (error) {
      throw error;
    }

    return value.phrases;
  }
}
